import React, { useMemo, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLanguage } from '../../../context/LanguageContext';
import {
  recipeService,
  flattenInstanceOptions,
  mapPlcValuesToFormValues,
} from '../../../services/procesos/recipeService';
import { assetService } from '../../../services/activos/assetService';
import { Card, CardHeader, CardContent } from '../../../components/ui/Card';
import { Loader2, Plus, FileStack, Layers, Pencil, Trash2, Save, Radio, X, Copy, RefreshCw } from 'lucide-react';
import { TemplateFormModal } from '../components/TemplateFormModal';
import { usePermissions } from '../../../hooks/usePermissions';
import { useFactory } from '../../../context/FactoryContext';

const TAB_PLANTILLAS = 'plantillas';
const TAB_INSTANCIAS = 'instancias';

/** Activos raíz (parent null) = fábricas. Acepta parent_id (API) o ParentID/parentID. */
function filterFactories(assets) {
  if (!assets || !Array.isArray(assets)) return [];
  return assets.filter((a) => {
    const pid = a.ParentID ?? a.parentID ?? a.parent_id;
    return pid == null || pid === '';
  });
}

/**
 * Líneas de producción de una fábrica: hijos directos (parent === factoryId).
 * Opcionalmente filtra por convención de código (Line_*, L01, etc.).
 * Acepta parent_id (API) o ParentID/parentID.
 */
function filterLinesByFactory(assets, factoryId) {
  if (!assets || !Array.isArray(assets) || factoryId == null) return [];
  const parentId = (a) => a.ParentID ?? a.parentID ?? a.parent_id;
  const withParent = assets.filter((a) => parentId(a) === factoryId);
  const byCode = withParent.filter((a) => {
    const code = (a.Code ?? a.code ?? '').toString();
    return (
      code.toLowerCase().includes('line') ||
      /^L\d+/i.test(code) ||
      /^Line_\d+/i.test(code)
    );
  });
  return byCode.length > 0 ? byCode : withParent;
}

/**
 * Todos los activos que pertenecen a la línea: la línea misma + descendientes (hijos recursivos).
 * Útil para listar activos seleccionables al definir parámetros de una plantilla.
 */
function getLineAssets(assets, lineId) {
  if (!assets || !Array.isArray(assets) || lineId == null) return [];
  const id = (a) => a.ID ?? a.id;
  const parentId = (a) => a.ParentID ?? a.parentID ?? a.parent_id;
  const result = [];
  const added = new Set();
  const queue = [lineId];
  while (queue.length > 0) {
    const pid = queue.shift();
    for (const a of assets) {
      if (parentId(a) !== pid) continue;
      const aid = id(a);
      if (added.has(aid)) continue;
      added.add(aid);
      result.push(a);
      queue.push(aid);
    }
  }
  return result;
}

const DatosMaestrosProcesos = () => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const { permissions, checkFactoryAccess } = usePermissions();
  const { selectedFactory } = useFactory();

  const [activeTab, setActiveTab] = useState(TAB_PLANTILLAS);
  const [selectedLine, setSelectedLine] = useState(null);
  const [templateModalMode, setTemplateModalMode] = useState(null);
  const [templateModalId, setTemplateModalId] = useState(null);
  const [templateToDeleteId, setTemplateToDeleteId] = useState(null);
  const [selectedInstanceId, setSelectedInstanceId] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedAlternative, setSelectedAlternative] = useState(null);
  const [editInstanceModalOpen, setEditInstanceModalOpen] = useState(false);
  const [sendToPlcModalOpen, setSendToPlcModalOpen] = useState(false);
  const [editInstanceDescription, setEditInstanceDescription] = useState('');
  const [editInstanceValues, setEditInstanceValues] = useState({});
  const [createInstanceTemplate, setCreateInstanceTemplate] = useState(null);
  const [createInstanceForm, setCreateInstanceForm] = useState({ product_name: '', alternative: '', revision: '', description: '', values: {} });
  const [createInstanceSourceId, setCreateInstanceSourceId] = useState('');
  const [createInstancePlcLoadError, setCreateInstancePlcLoadError] = useState(null);
  const [createInstancePlcValuesLoaded, setCreateInstancePlcValuesLoaded] = useState(false);
  const [templateParamFilter, setTemplateParamFilter] = useState('');

  const { data: assets = [], isLoading: loadingAssets } = useQuery({
    queryKey: ['assets'],
    queryFn: () => assetService.getAssets(),
    retry: 1,
  });

  const factories = useMemo(() => filterFactories(assets), [assets]);
  const factoryId = useMemo(() => {
    if (!selectedFactory) return null;
    const factoryAsset = factories.find((f) => (f.Code ?? f.code) === selectedFactory);
    return factoryAsset ? (factoryAsset.ID ?? factoryAsset.id) : null;
  }, [factories, selectedFactory]);

  const lines = useMemo(
    () => filterLinesByFactory(assets, factoryId),
    [assets, factoryId]
  );

  useEffect(() => {
    setSelectedLine(null);
  }, [factoryId]);

  const lineIdentifier = selectedLine
    ? selectedLine.ID ?? selectedLine.id
    : null;

  const lineAssets = useMemo(
    () => getLineAssets(assets, lineIdentifier),
    [assets, lineIdentifier]
  );

  // Lista de plantillas filtrada por la línea seleccionada (backend usa line_tech_code como line_asset_id)
  const {
    data: templates = [],
    isLoading: loadingTemplates,
    isError: errorTemplates,
    error: templatesError,
  } = useQuery({
    queryKey: ['recipe-templates', lineIdentifier],
    queryFn: () => recipeService.listTemplatesByLine(lineIdentifier),
    enabled: !!lineIdentifier,
    retry: 1,
  });
  const templatesForLine = lineIdentifier ? templates : [];

  const lineTechCodeForCreate = selectedLine
    ? selectedLine.tech_code ?? selectedLine.TechCode ?? selectedLine.techCode ?? selectedLine.ID ?? selectedLine.id
    : null;

  useEffect(() => {
    if (!lineTechCodeForCreate) {
      setSelectedInstanceId(null);
      setSelectedProduct(null);
      setSelectedAlternative(null);
    }
  }, [lineTechCodeForCreate]);

  useEffect(() => {
    setTemplateParamFilter('');
  }, [lineIdentifier]);

  const { data: instanceGroups = [], isLoading: loadingInstanceGroups } = useQuery({
    queryKey: ['recipe-instances-by-line', lineTechCodeForCreate],
    queryFn: () => recipeService.listInstancesByLine(lineTechCodeForCreate),
    enabled: !!lineTechCodeForCreate && (activeTab === TAB_INSTANCIAS || !!createInstanceTemplate),
    retry: 1,
  });

  const createInstanceSourceOptions = useMemo(
    () => flattenInstanceOptions(instanceGroups),
    [instanceGroups]
  );

  const {
    data: instanceFromGet = null,
    isLoading: loadingInstanceFromGet,
  } = useQuery({
    queryKey: ['recipe-instance', selectedInstanceId],
    queryFn: () => recipeService.getInstance(selectedInstanceId),
    enabled: activeTab === TAB_INSTANCIAS && selectedInstanceId != null,
    retry: 1,
  });

  const {
    data: instanceDetail = null,
    isLoading: loadingInstance,
    isFetching: fetchingReadFromPlc,
    isError: errorInstance,
    error: instanceError,
    refetch: refetchReadFromPlc,
  } = useQuery({
    queryKey: ['recipe-read-from-plc', selectedInstanceId],
    queryFn: () => recipeService.readFromPLC(selectedInstanceId),
    enabled: activeTab === TAB_INSTANCIAS && selectedInstanceId != null,
    retry: 1,
  });

  const updateInstanceMutation = useMutation({
    mutationFn: ({ id, body }) => recipeService.updateInstance(id, body),
    onSuccess: (_, { id: instanceId }) => {
      queryClient.invalidateQueries({ queryKey: ['recipe-read-from-plc', instanceId] });
      queryClient.invalidateQueries({ queryKey: ['recipe-instance', instanceId] });
      setEditInstanceModalOpen(false);
    },
    onError: (err) => {
      throw err;
    },
  });

  const deleteInstanceMutation = useMutation({
    mutationFn: (id) => recipeService.deleteInstance(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipe-instances-by-line'] });
      setSelectedInstanceId(null);
      setSelectedProduct(null);
      setSelectedAlternative(null);
    },
    onError: (err) => {
      throw err;
    },
  });

  const sendToPlcMutation = useMutation({
    mutationFn: (instanceId) => recipeService.sendToPlc(instanceId),
    onSuccess: (_, instanceId) => {
      queryClient.invalidateQueries({ queryKey: ['recipe-read-from-plc', instanceId] });
      setSendToPlcModalOpen(false);
    },
    onError: (err) => {
      throw err;
    },
  });

  const loadPlcValuesForCreateMutation = useMutation({
    mutationFn: ({ sourceInstanceId }) => recipeService.readFromPLC(sourceInstanceId),
    onSuccess: (plcData, { templateParameters }) => {
      setCreateInstancePlcLoadError(null);
      setCreateInstancePlcValuesLoaded(true);
      const templateParams = templateParameters ?? [];
      const mapped = mapPlcValuesToFormValues(plcData, templateParams, 'actual');
      const emptyValues = Object.fromEntries(templateParams.map((p) => [p.id ?? p.ID, '']));
      setCreateInstanceForm((f) => ({
        ...f,
        values: { ...emptyValues, ...mapped },
      }));
    },
    onError: (err) => {
      setCreateInstancePlcValuesLoaded(false);
      setCreateInstancePlcLoadError(
        err?.response?.data?.error ?? err?.message ?? 'Error al leer valores del PLC'
      );
    },
  });

  const createInstanceMutation = useMutation({
    mutationFn: async ({ sourceInstanceId, templateParameters, ...body }) => {
      if (sourceInstanceId) {
        return recipeService.createInstanceFromPlcSource({
          sourceInstanceId,
          templateParameters,
          body,
          valueField: 'actual',
        });
      }
      const values = (templateParameters ?? []).map((p) => ({
        parameter_id: p.id,
        value: body.values?.[p.id] ?? '',
      }));
      return recipeService.createInstance({
        template_id: body.template_id,
        product_name: body.product_name,
        alternative: body.alternative,
        revision: body.revision,
        description: body.description,
        values,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipe-instances-by-line'] });
      setCreateInstanceTemplate(null);
      setCreateInstanceForm({ product_name: '', alternative: '', revision: '', description: '', values: {} });
      setCreateInstanceSourceId('');
      setCreateInstancePlcLoadError(null);
      setCreateInstancePlcValuesLoaded(false);
    },
    onError: (err) => {
      throw err;
    },
  });

  const handleOpenCreateInstance = (template) => {
    const templateParams = template.parameters ?? [];
    const defaultSourceId =
      selectedInstanceId != null ? String(selectedInstanceId) : '';
    setCreateInstanceTemplate(template);
    setCreateInstanceSourceId(defaultSourceId);
    setCreateInstancePlcLoadError(null);
    setCreateInstancePlcValuesLoaded(false);
    setCreateInstanceForm({
      product_name: '',
      alternative: '',
      revision: '',
      description: '',
      values: Object.fromEntries(templateParams.map((p) => [p.id, ''])),
    });
    if (defaultSourceId) {
      handleLoadPlcValuesForCreate(defaultSourceId, templateParams);
    }
  };

  const handleCloseCreateInstance = () => {
    setCreateInstanceTemplate(null);
    setCreateInstanceSourceId('');
    setCreateInstancePlcLoadError(null);
    setCreateInstancePlcValuesLoaded(false);
    setCreateInstanceForm({ product_name: '', alternative: '', revision: '', description: '', values: {} });
  };

  const handleLoadPlcValuesForCreate = (sourceId, templateParameters) => {
    if (!sourceId) return;
    loadPlcValuesForCreateMutation.mutate({
      sourceInstanceId: Number(sourceId),
      templateParameters: templateParameters ?? createInstanceTemplate?.parameters ?? [],
    });
  };

  const handleCreateInstanceSourceChange = (rawSourceId) => {
    setCreateInstanceSourceId(rawSourceId);
    setCreateInstancePlcLoadError(null);
    setCreateInstancePlcValuesLoaded(false);
    if (!rawSourceId) {
      const templateParams = createInstanceTemplate?.parameters ?? [];
      setCreateInstanceForm((f) => ({
        ...f,
        values: Object.fromEntries(templateParams.map((p) => [p.id ?? p.ID, ''])),
      }));
      return;
    }
    handleLoadPlcValuesForCreate(rawSourceId, createInstanceTemplate?.parameters);
  };

  useEffect(() => {
    if (editInstanceModalOpen && instanceFromGet) {
      setEditInstanceDescription(instanceFromGet.description ?? '');
      const valuesMap = {};
      for (const v of instanceFromGet.values ?? []) {
        const pid = v.parameter_id ?? v.parameterID;
        if (pid != null) valuesMap[pid] = v.value ?? '';
      }
      setEditInstanceValues(valuesMap);
    }
  }, [editInstanceModalOpen, instanceFromGet]);

  const createTemplateMutation = useMutation({
    mutationFn: (body) => recipeService.createTemplateWithParams(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipe-templates'] });
      setTemplateModalMode(null);
      setTemplateModalId(null);
    },
    onError: (err) => {
      throw err;
    },
  });

  const updateTemplateMutation = useMutation({
    mutationFn: ({ id, body }) => recipeService.updateTemplateWithParams(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipe-templates'] });
      queryClient.invalidateQueries({ queryKey: ['recipe-template', templateModalId] });
      setTemplateModalMode(null);
      setTemplateModalId(null);
    },
    onError: (err) => {
      throw err;
    },
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: (id) => recipeService.deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipe-templates'] });
      setTemplateToDeleteId(null);
    },
    onError: (err) => {
      setTemplateToDeleteId(null);
      const msg = err.response?.data?.error ?? err.message ?? 'Error al eliminar';
      alert(msg);
    },
  });

  const handleOpenCreateTemplate = () => {
    setTemplateModalId(null);
    setTemplateModalMode('create');
  };

  const handleOpenEditTemplate = (template) => {
    setTemplateModalId(template.id);
    setTemplateModalMode('edit');
  };

  const handleCloseTemplateModal = () => {
    setTemplateModalMode(null);
    setTemplateModalId(null);
  };

  const handleSaveCreateTemplate = async (payload) => {
    await createTemplateMutation.mutateAsync(payload);
  };

  const handleSaveUpdateTemplate = async (payload) => {
    if (templateModalId) {
      await updateTemplateMutation.mutateAsync({ id: templateModalId, body: payload });
    }
  };

  const handleDeleteTemplate = (template) => {
    const msg =
      'Delete template "' +
      (template.name || '') +
      '"? This cannot be undone.';
    if (window.confirm(msg)) {
      setTemplateToDeleteId(template.id);
      deleteTemplateMutation.mutate(template.id);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold text-white mb-2">
        {t?.datosMaestros?.title ?? 'Datos Maestros de Procesos'}
      </h1>
      <p className="text-gray-400 text-sm mb-6">
        {t?.datosMaestros?.description ?? 'Datos maestros de procesos'}
      </p>

      {/* Contexto: selección de línea (común a Plantillas e Instancias) */}
      <Card className="mb-6">
        <CardHeader>
          <span className="text-lg font-semibold text-white">Contexto</span>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-400">Fábrica</label>
              <span className="bg-gray-800 border border-gray-600 text-white rounded-lg px-3 py-2 min-w-[200px]">
                {selectedFactory || 'Selecciona una fábrica en el header'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-400">Línea</label>
              <select
                value={String(lineIdentifier ?? '')}
                onChange={(e) => {
                  const raw = e.target.value;
                  const id = raw ? Number(raw) : null;
                  const line = id != null ? lines.find((l) => Number(l.ID ?? l.id) === Number(id)) : null;
                  setSelectedLine(line ?? null);
                }}
                disabled={!factoryId}
                className="bg-gray-800 border border-gray-600 text-white rounded-lg px-3 py-2 min-w-[200px] focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">
                  {factoryId ? 'Seleccionar línea' : 'Primero elige una fábrica'}
                </option>
                {lines.map((line) => {
                  const id = line.ID ?? line.id;
                  const code = line.Code ?? line.code ?? '';
                  const name = line.Name ?? line.name ?? '';
                  const lineLabel = name || code || `ID ${id}`;
                  return (
                    <option key={id} value={String(id)}>
                      {lineLabel}
                    </option>
                  );
                })}
              </select>
            </div>
            {lineIdentifier && (
              <span className="text-sm text-gray-500">
                Datos para esta línea
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-2 border-b border-gray-700 mb-6">
        <button
          type="button"
          onClick={() => setActiveTab(TAB_PLANTILLAS)}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
            activeTab === TAB_PLANTILLAS
              ? 'bg-gray-700 text-white border border-b-0 border-gray-600'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <FileStack className="w-4 h-4 inline-block mr-2" />
          Plantillas
        </button>
        <button
          type="button"
          onClick={() => setActiveTab(TAB_INSTANCIAS)}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
            activeTab === TAB_INSTANCIAS
              ? 'bg-gray-700 text-white border border-b-0 border-gray-600'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Layers className="w-4 h-4 inline-block mr-2" />
          Instancias
        </button>
      </div>

      {activeTab === TAB_PLANTILLAS && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <span className="text-lg font-semibold text-white">Plantillas de receta</span>
            {lineIdentifier && !loadingTemplates && templatesForLine.length === 0 && (
              <button
                type="button"
                onClick={handleOpenCreateTemplate}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4" />
                New template
              </button>
            )}
            {lineIdentifier && !loadingTemplates && templatesForLine.length > 0 && (
              <span className="text-sm text-gray-400">Active template for this line</span>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {!lineIdentifier && (
              <p className="text-gray-500 text-sm">
                {factoryId
                  ? 'Select a line to view and manage templates.'
                  : 'Select a factory and then a line to view and manage templates.'}
              </p>
            )}

            {lineIdentifier && loadingTemplates && (
              <div className="flex items-center gap-2 text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin" />
                Loading templates…
              </div>
            )}

            {lineIdentifier && errorTemplates && (
              <p className="text-red-400 text-sm">
                {templatesError?.message ?? 'Error loading templates'}
              </p>
            )}

            {lineIdentifier && !loadingTemplates && !errorTemplates && (
              <div className="space-y-4">
                {templatesForLine.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="mb-4 rounded-2xl bg-blue-500/10 p-4">
                      <FileStack className="w-8 h-8 text-blue-400" />
                    </div>
                    <p className="text-lg font-semibold text-white mb-1">
                      This line has no template yet
                    </p>
                    <p className="text-gray-400 text-sm mb-6 max-w-md">
                      Define a recipe template for this line and use it as a base
                      to generate consistent instances.
                    </p>
                    
                  </div>
                ) : (
                  (() => {
                    const template = templatesForLine[0];
                    const paramCount = Array.isArray(template.parameters) ? template.parameters.length : 0;
                    const filteredParams = (template.parameters || []).filter((p) =>
                      (p.name ?? '').toLowerCase().includes(templateParamFilter.toLowerCase().trim())
                    );

                    return (
                      <div className="space-y-4">
                        <div className="rounded-2xl overflow-hidden border border-gray-700/80 bg-blue-900/50 shadow-xl shadow-black/40">
                          <div className="px-6 py-5 flex flex-wrap items-start justify-between gap-4">
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 rounded-2xl bg-blue-500/30 flex items-center justify-center shrink-0">
                                <FileStack className="w-6 h-6 text-blue-100" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-xs uppercase tracking-[0.2em] text-blue-200/80 mb-1">
                                  Recipe template · Line
                                </p>
                                <h2 className="text-2xl font-bold text-white leading-tight">
                                  {template.name ?? 'No name'}
                                </h2>
                                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                                  {template.version && (
                                    <span className="px-2.5 py-1 rounded-full bg-black/40 border border-blue-300/40 text-blue-100">
                                      v{template.version}
                                    </span>
                                  )}
                                  <span className="px-2.5 py-1 rounded-full bg-black/30 border border-gray-500/60 text-gray-200">
                                    {paramCount} parameter{paramCount !== 1 ? 's' : ''}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleOpenCreateInstance(template)}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-emerald-400/60 text-emerald-50 bg-emerald-500/10 hover:bg-emerald-500/20 text-sm font-medium"
                              >
                                <Copy className="w-4 h-4" />
                                Create instance
                              </button>
                              <button
                                type="button"
                                onClick={() => handleOpenEditTemplate(template)}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-400/60 text-gray-50 bg-black/20 hover:bg-black/30 text-sm font-medium"
                              >
                                <Pencil className="w-4 h-4" />
                                Edit template
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteTemplate(template)}
                                disabled={templateToDeleteId === template.id && deleteTemplateMutation.isPending}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-red-500/60 text-red-100 bg-red-500/10 hover:bg-red-500/20 text-sm font-medium disabled:opacity-50"
                              >
                                {templateToDeleteId === template.id && deleteTemplateMutation.isPending ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                                Delete
                              </button>
                            </div>
                          </div>

                          <div className="px-6 pb-5">
                            <div className="rounded-2xl bg-black/35 border border-white/5 px-4 py-3">
                              {template.description ? (
                                <p className="text-sm text-gray-100 leading-relaxed">
                                  {template.description}
                                </p>
                              ) : (
                                <p className="text-sm text-gray-400 italic">
                                  No description for this template.
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        {paramCount > 0 && (
                          <div className="rounded-2xl border border-gray-700/80 bg-gray-900/70 backdrop-blur">
                            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-gray-700/80">
                              <div>
                                <p className="text-xs font-semibold text-gray-400 uppercase tracking-[0.2em]">
                                  Template parameters
                                </p>
                                <p className="text-sm text-gray-300">
                                  Base values that instances will inherit.
                                </p>
                              </div>
                              <div className="w-full sm:w-64">
                                <input
                                  type="text"
                                  placeholder="Search parameter…"
                                  value={templateParamFilter}
                                  onChange={(e) => setTemplateParamFilter(e.target.value)}
                                  className="w-full px-3 py-1.5 rounded-lg bg-gray-800 border border-gray-600 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                            </div>
                            <div className="max-h-80 overflow-y-auto">
                              {filteredParams.length > 0 ? (
                                <table className="w-full text-xs">
                                  <thead className="sticky top-0 z-10 bg-gray-900/90">
                                    <tr className="border-b border-gray-800">
                                      <th className="text-left py-2 px-4 text-gray-400 font-semibold uppercase tracking-wider">
                                        Parameter
                                      </th>
                                      <th className="text-left py-2 px-4 text-gray-400 font-semibold uppercase tracking-wider w-40">
                                        PLC Address
                                      </th>
                                      <th className="text-left py-2 px-4 text-gray-400 font-semibold uppercase tracking-wider w-24">
                                        Unit
                                      </th>
                                      <th className="text-left py-2 px-4 text-gray-400 font-semibold uppercase tracking-wider w-32">
                                        Data_type
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {filteredParams.map((p) => {
                                      const plcAddress = p.plc_address ?? p.plcAddress ?? '';
                                      const unit = (p.unit && p.unit !== '_' ? p.unit : '—');
                                      const dataType = p.data_type ?? p.dataType ?? '—';
                                      return (
                                        <tr
                                          key={p.id}
                                          className="border-b border-gray-800 hover:bg-gray-800/60 transition-colors"
                                        >
                                          <td className="py-2 px-4 text-white text-sm truncate">
                                            {p.name}
                                          </td>
                                          <td className="py-2 px-4 text-gray-200 font-mono truncate">
                                            {plcAddress || '—'}
                                          </td>
                                          <td className="py-2 px-4 text-gray-200">
                                            {unit}
                                          </td>
                                          <td className="py-2 px-4 text-gray-200">
                                            {dataType}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              ) : (
                                <div className="py-8 text-center text-gray-500 text-sm">
                                  No parameters match the search.
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === TAB_INSTANCIAS && (
        <Card className="overflow-hidden border border-gray-700/80 shadow-xl shadow-black/20">
          {!lineIdentifier && (
            <CardContent className="py-12">
              <p className="text-gray-500 text-center text-sm">Select a line above to see instances.</p>
            </CardContent>
          )}
          {lineIdentifier && loadingInstanceGroups && (
            <CardContent className="py-16">
              <div className="flex flex-col items-center justify-center gap-3 text-gray-400">
                <Loader2 className="w-10 h-10 animate-spin text-blue-400" />
                <p className="text-sm font-medium">Loading instances…</p>
              </div>
            </CardContent>
          )}
          {lineIdentifier && !loadingInstanceGroups && (
            <>
              {instanceGroups.length > 0 && (() => {
                const productOptions = instanceGroups.map((g) => g.product_name ?? g.ProductName ?? '—').filter(Boolean);
                const selectedProductGroup = instanceGroups.find(
                  (g) => (g.product_name ?? g.ProductName ?? '—') === selectedProduct
                );
                const alternativeOptions = (selectedProductGroup?.alternatives ?? selectedProductGroup?.Alternatives ?? []).map(
                  (a) => a.alternative ?? a.Alternative ?? '—'
                ).filter(Boolean);
                const selectedAltGroup = (selectedProductGroup?.alternatives ?? selectedProductGroup?.Alternatives ?? []).find(
                  (a) => (a.alternative ?? a.Alternative ?? '—') === selectedAlternative
                );
                const revisions = selectedAltGroup?.revision ?? selectedAltGroup?.Revision ?? selectedAltGroup?.revisions ?? [];
                const revisionOptions = Array.isArray(revisions) ? revisions : [];

                return (
                  <div className="border-b border-gray-700/80 bg-gray-800/30 px-5 py-4">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                      Select product, alternative and revision
                    </p>
                    <div className="flex flex-wrap items-center gap-4">
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-400">Product</label>
                        <select
                          value={selectedProduct ?? ''}
                          onChange={(e) => {
                            const v = e.target.value || null;
                            setSelectedProduct(v);
                            setSelectedAlternative(null);
                            setSelectedInstanceId(null);
                          }}
                          className="bg-gray-800 border border-gray-600 text-white rounded-lg px-3 py-2 min-w-[180px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select product</option>
                          {productOptions.map((name) => (
                            <option key={name} value={name}>{name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-400">Alternative</label>
                        <select
                          value={selectedAlternative ?? ''}
                          onChange={(e) => {
                            const v = e.target.value || null;
                            setSelectedAlternative(v);
                            setSelectedInstanceId(null);
                          }}
                          disabled={!selectedProduct}
                          className="bg-gray-800 border border-gray-600 text-white rounded-lg px-3 py-2 min-w-[180px] focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <option value="">{selectedProduct ? 'Select alternative' : '—'}</option>
                          {alternativeOptions.map((name) => (
                            <option key={name} value={name}>{name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-400">Revision</label>
                        <select
                          value={selectedInstanceId ?? ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setSelectedInstanceId(v ? Number(v) : null);
                          }}
                          disabled={!selectedAlternative}
                          className="bg-gray-800 border border-gray-600 text-white rounded-lg px-3 py-2 min-w-[180px] focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <option value="">{selectedAlternative ? 'Select revision' : '—'}</option>
                          {revisionOptions.map((rev) => {
                            const instanceId = rev.instance_id ?? rev.InstanceID ?? rev.instanceId;
                            const revLabel = rev.revision ?? rev.Revision ?? '—';
                            if (instanceId == null) return null;
                            return (
                              <option key={instanceId} value={instanceId}>
                                {revLabel}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </div>
                  </div>
                );
              })()}
              {instanceGroups.length === 0 && (
                <CardContent className="py-8 border-b border-gray-700/80">
                  <p className="text-gray-500 text-center text-sm">No instances for this line.</p>
                </CardContent>
              )}
              {selectedInstanceId == null ? (
                <CardContent className="py-12">
                  <p className="text-gray-500 text-center text-sm">Select an instance from the list to see details.</p>
                </CardContent>
              ) : (
                <>
                  {loadingInstanceFromGet && (
                    <CardContent className="py-16">
                      <div className="flex flex-col items-center justify-center gap-3 text-gray-400">
                        <Loader2 className="w-10 h-10 animate-spin text-blue-400" />
                        <p className="text-sm font-medium">Loading instance…</p>
                      </div>
                    </CardContent>
                  )}
                  {errorInstance && (
                    <div className="px-6 py-3 bg-red-900/40 border-b border-red-500/60">
                      <p className="text-red-200 text-sm text-center">
                        {instanceError?.response?.data?.error ?? instanceError?.message ?? 'Error loading instance from PLC'}
                      </p>
                    </div>
                  )}
                  {!loadingInstanceFromGet && instanceFromGet && (
                    <>
                      <div className="bg-gradient-to-r from-gray-800/90 to-gray-900/90 border-b border-gray-700/80 px-6 py-5">
                        <div className="flex flex-wrap items-start justify-between gap-4">
                          <div>
                            <h2 className="text-xl font-bold text-white tracking-tight">
                              {instanceFromGet.product_name ?? instanceFromGet.ProductName ?? '—'} / {instanceFromGet.alternative ?? instanceFromGet.Alternative ?? '—'} / {instanceFromGet.revision ?? instanceFromGet.Revision ?? '—'}
                            </h2>
                            <p className="text-gray-400 text-sm mt-1 max-w-xl">
                              {instanceFromGet.description || 'No description'}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setEditInstanceModalOpen(true)}
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors text-sm font-medium"
                            >
                              <Pencil className="w-4 h-4" />
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => setSendToPlcModalOpen(true)}
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors text-sm font-medium"
                            >
                              <Radio className="w-4 h-4" />
                              Send to PLC
                            </button>
                            <button
                              type="button"
                              onClick={() => refetchReadFromPlc()}
                              disabled={fetchingReadFromPlc}
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors text-sm font-medium disabled:opacity-50"
                            >
                              {fetchingReadFromPlc ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <RefreshCw className="w-4 h-4" />
                              )}
                              Actualizar
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                if (!selectedInstanceId || deleteInstanceMutation.isPending) return;
                                const msg = 'Delete this instance? This cannot be undone.';
                                if (window.confirm(msg)) {
                                  deleteInstanceMutation.mutate(selectedInstanceId);
                                }
                              }}
                              disabled={deleteInstanceMutation.isPending}
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50"
                            >
                              {deleteInstanceMutation.isPending ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                      <CardContent className="p-0">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-gray-800/70 border-b border-gray-700/80">
                                <th className="text-left py-4 px-5 text-gray-400 font-semibold uppercase tracking-wider text-xs">
                                  Parameter
                                </th>
                                <th className="text-left py-4 px-5 text-gray-400 font-semibold uppercase tracking-wider text-xs w-40">
                                  Recipe BD Value
                                </th>
                                <th className="text-left py-4 px-5 text-gray-400 font-semibold uppercase tracking-wider text-xs w-40">
                                  Recipe PLC Value
                                </th>
                                <th className="text-left py-4 px-5 text-gray-400 font-semibold uppercase tracking-wider text-xs w-40">
                                  Actual PLC Value
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {(instanceFromGet.values ?? []).map((v) => {
                                const pid = v.parameter_id ?? v.parameterID;
                                const valorGuardado = v.value ?? '—';
                                const plcRow = instanceDetail?.values?.find((x) => (x.parameter_id ?? x.parameterID) === pid);
                                const recipe = plcRow?.recipe ?? '—';
                                const actual = plcRow?.actual ?? '—';
                                const unit =
                                  (v.unit && v.unit !== '_' ? v.unit : '') ||
                                  (plcRow?.unit && plcRow.unit !== '_' ? plcRow.unit : '');
                                const templateParam =
                                  instanceFromGet.template?.parameters?.find(
                                    (p) => (p.id ?? p.ID) === pid
                                  ) ?? null;
                                const plcAddress =
                                  templateParam?.plc_address ??
                                  templateParam?.plcAddress ??
                                  plcRow?.plc_address ??
                                  plcRow?.plcAddress ??
                                  v.plc_address ??
                                  v.plcAddress ??
                                  '';
                                return (
                                  <tr
                                    key={pid}
                                    className="border-b border-gray-700/50 hover:bg-gray-800/40 transition-colors"
                                  >
                                    <td className="py-3.5 px-5">
                                      <div className="flex items-center justify-between gap-3">
                                        <div className="font-medium text-white">
                                          {v.parameter_name ?? v.parameterName ?? '—'}
                                        </div>
                                        <div className="flex flex-row items-center gap-1">
                                          {plcAddress && (
                                            <span className="px-2 py-0.5 rounded-full bg-gray-900 text-gray-200 border border-gray-700 text-[10px] font-mono">
                                              {plcAddress}
                                            </span>
                                          )}
                                          {unit && (
                                            <span className="px-2 py-0.5 rounded-full bg-gray-800 text-gray-200 border border-gray-600 text-[10px] uppercase tracking-wide">
                                              {unit}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                    <td className="py-3.5 px-5">
                                      <span className="text-gray-300 bg-gray-800/80 rounded-md px-2.5 py-1.5 inline-block font-mono text-base">
                                        {valorGuardado}
                                      </span>
                                    </td>
                                    <td className="py-3.5 px-5">
                                      <span className="text-amber-200/90 bg-amber-500/10 rounded-md px-2.5 py-1.5 inline-block font-mono text-base">
                                        {recipe}
                                      </span>
                                    </td>
                                    <td className="py-3.5 px-5">
                                      <span className="text-emerald-300/90 bg-emerald-500/10 rounded-md px-2.5 py-1.5 inline-block font-mono text-base">
                                        {actual}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        {(instanceFromGet.values ?? []).length === 0 && (
                          <div className="py-12 text-center text-gray-500 text-sm">
                            No parameters in this instance.
                          </div>
                        )}
                        {updateInstanceMutation.isError && (
                          <p className="px-5 py-3 text-red-400 text-sm border-t border-gray-700/80">
                            {updateInstanceMutation.error?.response?.data?.error ??
                              updateInstanceMutation.error?.message ??
                              'Error saving'}
                          </p>
                        )}
                      </CardContent>
                    </>
                  )}
                  {!loadingInstanceFromGet && !instanceFromGet && selectedInstanceId != null && (
                    <CardContent className="py-12">
                      <p className="text-gray-500 text-center text-sm">Instance not found.</p>
                    </CardContent>
                  )}
                </>
              )}
            </>
          )}
        </Card>
      )}

      {templateModalMode === 'create' && (
        <TemplateFormModal
          mode="create"
          lineAssetTechCode={lineTechCodeForCreate}
          lineAssets={lineAssets}
          lineId={lineIdentifier}
          onClose={handleCloseTemplateModal}
          onSave={handleSaveCreateTemplate}
          isSaving={createTemplateMutation.isPending}
          error={createTemplateMutation.error}
        />
      )}

      {templateModalMode === 'edit' && templateModalId && (
        <TemplateFormModal
          mode="edit"
          templateId={templateModalId}
          lineAssets={lineAssets}
          lineId={lineIdentifier}
          onClose={handleCloseTemplateModal}
          onSave={handleSaveUpdateTemplate}
          isSaving={updateTemplateMutation.isPending}
          error={updateTemplateMutation.error}
        />
      )}

      {editInstanceModalOpen && selectedInstanceId != null && instanceFromGet && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setEditInstanceModalOpen(false)}>
          <div className="bg-gray-800/95 rounded-2xl shadow-2xl shadow-black/50 max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-gray-700/80" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700/80 bg-gray-900/40">
              <h3 className="text-lg font-semibold text-white">Edit instance</h3>
              <button type="button" onClick={() => setEditInstanceModalOpen(false)} className="p-2 rounded-xl text-gray-400 hover:text-white hover:bg-gray-700/80 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1.5">Description</label>
                <textarea
                  value={editInstanceDescription}
                  onChange={(e) => setEditInstanceDescription(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2.5 bg-gray-900 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/60 focus:border-blue-500/50 text-sm transition-shadow"
                  placeholder="Instance description"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Parameter values</label>
                <div className="space-y-2">
                  {(instanceFromGet.values ?? []).map((v) => {
                    const pid = v.parameter_id ?? v.parameterID;
                    const paramName = v.parameter_name ?? v.parameterName ?? '—';
                    const unit = (v.unit && v.unit !== '_' ? v.unit : '') || '';
                    const dataType = v.data_type ?? v.dataType ?? '';
                    const plcAddress = v.plc_address ?? v.plcAddress ?? '';
                    const sub = [
                      plcAddress ? `PLC: ${plcAddress}` : null,
                      unit,
                      dataType,
                    ].filter(Boolean).join(' · ') || null;
                    return (
                      <div key={pid} className="flex items-center gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-white truncate">{paramName}</p>
                          {sub && <p className="text-xs text-gray-500 truncate">{sub}</p>}
                        </div>
                        <input
                          type="text"
                          value={editInstanceValues[pid] ?? ''}
                          onChange={(e) => setEditInstanceValues((prev) => ({ ...prev, [pid]: e.target.value }))}
                          className="w-40 shrink-0 px-3 py-2 bg-gray-900 border border-gray-600 rounded-xl text-white text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/60 focus:border-blue-500/50 transition-shadow"
                          placeholder="Value"
                        />
                      </div>
                    );
                  })}
                </div>
                {(instanceFromGet.values ?? []).length === 0 && (
                  <p className="text-sm text-gray-500 italic">No parameters in this instance.</p>
                )}
              </div>
              {updateInstanceMutation.isError && (
                <p className="text-red-400 text-sm">{updateInstanceMutation.error?.response?.data?.error ?? updateInstanceMutation.error?.message ?? 'Error saving'}</p>
              )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-700/80 bg-gray-900/30">
              <button type="button" onClick={() => setEditInstanceModalOpen(false)} className="px-4 py-2.5 rounded-xl border border-gray-600 text-gray-300 hover:bg-gray-700/80 hover:text-white transition-colors text-sm font-medium">
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const values = (instanceFromGet.values ?? []).map((v) => {
                    const pid = v.parameter_id ?? v.parameterID;
                    return { parameter_id: pid, value: editInstanceValues[pid] ?? v.value ?? '' };
                  });
                  updateInstanceMutation.mutate({
                    id: selectedInstanceId,
                    body: { description: editInstanceDescription, values },
                  });
                }}
                disabled={updateInstanceMutation.isPending}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50 text-sm font-medium transition-colors"
              >
                {updateInstanceMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {sendToPlcModalOpen && selectedInstanceId != null && instanceFromGet && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setSendToPlcModalOpen(false)}>
          <div className="bg-gray-800/95 rounded-2xl shadow-2xl shadow-black/50 max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-gray-700/80" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700/80 bg-gray-900/40">
              <h3 className="text-lg font-semibold text-white">Send recipe to PLC</h3>
              <button type="button" onClick={() => setSendToPlcModalOpen(false)} className="p-2 rounded-xl text-gray-400 hover:text-white hover:bg-gray-700/80 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <p className="text-gray-400 text-sm mb-4">The following values will be sent to the PLC:</p>
              <div className="overflow-x-auto rounded-xl border border-gray-700/80">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-700/80 bg-gray-900/50">
                      <th className="text-left py-3 px-4 text-gray-400 font-semibold text-xs uppercase tracking-wider">Parameter</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-semibold text-xs uppercase tracking-wider w-32">Recipe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(instanceFromGet.values ?? []).map((v) => {
                      const pid = v.parameter_id ?? v.parameterID;
                      const value = v.value ?? '—';
                      return (
                        <tr key={pid} className="border-b border-gray-700/50 last:border-0 hover:bg-gray-800/50 transition-colors">
                          <td className="py-3 px-4 text-white">{v.parameter_name ?? v.parameterName ?? '—'}</td>
                          <td className="py-3 px-4 text-gray-300 font-mono text-xs">{value}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {sendToPlcMutation.isError && (
                <p className="text-red-400 text-sm mt-4">{sendToPlcMutation.error?.response?.data?.error ?? sendToPlcMutation.error?.message ?? 'Error sending'}</p>
              )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-700/80 bg-gray-900/30">
              <button type="button" onClick={() => setSendToPlcModalOpen(false)} className="px-4 py-2.5 rounded-xl border border-gray-600 text-gray-300 hover:bg-gray-700/80 hover:text-white transition-colors text-sm font-medium">
                Cancel
              </button>
              <button
                type="button"
                onClick={() => sendToPlcMutation.mutate(selectedInstanceId)}
                disabled={sendToPlcMutation.isPending}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50 text-sm font-medium transition-colors"
              >
                {sendToPlcMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save values
              </button>
            </div>
          </div>
        </div>
      )}

      {createInstanceTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={handleCloseCreateInstance}>
          <div className="bg-gray-800/95 rounded-2xl shadow-2xl shadow-black/50 max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col border border-gray-700/80" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700/80 bg-gray-900/40">
              <h3 className="text-lg font-semibold text-white">Create instance from &quot;{createInstanceTemplate.name ?? 'Template'}&quot;</h3>
              <button type="button" onClick={handleCloseCreateInstance} className="p-2 rounded-xl text-gray-400 hover:text-white hover:bg-gray-700/80 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              <div className="rounded-xl border border-gray-700/80 bg-gray-900/40 p-4 space-y-3">
                <p className="text-sm font-medium text-gray-300">Copiar valores desde instancia (PLC actual)</p>
                {loadingInstanceGroups ? (
                  <div className="flex items-center gap-2 text-gray-400 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Cargando instancias…
                  </div>
                ) : createInstanceSourceOptions.length === 0 ? (
                  <p className="text-sm text-gray-500">No hay instancias en esta línea. Rellena los valores manualmente.</p>
                ) : (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <select
                      value={createInstanceSourceId}
                      onChange={(e) => handleCreateInstanceSourceChange(e.target.value)}
                      className="flex-1 bg-gray-900 border border-gray-600 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/60"
                    >
                      <option value="">Sin instancia origen (manual)</option>
                      {createInstanceSourceOptions.map((opt) => (
                        <option key={opt.instanceId} value={String(opt.instanceId)}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() =>
                        handleLoadPlcValuesForCreate(
                          createInstanceSourceId,
                          createInstanceTemplate.parameters
                        )
                      }
                      disabled={
                        !createInstanceSourceId ||
                        loadPlcValuesForCreateMutation.isPending
                      }
                      className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border border-emerald-500/50 text-emerald-100 bg-emerald-500/10 hover:bg-emerald-500/20 text-sm font-medium disabled:opacity-50 shrink-0"
                    >
                      {loadPlcValuesForCreateMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                      Cargar PLC
                    </button>
                  </div>
                )}
                {createInstancePlcValuesLoaded && (
                  <p className="text-xs text-emerald-400/90">
                    Valores cargados desde el PLC. No se pueden modificar; indica producto, alternativa y revisión nuevos y pulsa Crear.
                  </p>
                )}
                {createInstanceSourceId && !createInstancePlcValuesLoaded && !loadPlcValuesForCreateMutation.isPending && (
                  <p className="text-xs text-gray-500">
                    Selecciona una instancia y espera a que se carguen los valores del PLC.
                  </p>
                )}
                {createInstancePlcLoadError && (
                  <p className="text-red-400 text-sm">{createInstancePlcLoadError}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1.5">Product name</label>
                <input
                  type="text"
                  value={createInstanceForm.product_name}
                  onChange={(e) => setCreateInstanceForm((f) => ({ ...f, product_name: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-gray-900 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/60 focus:border-blue-500/50 text-sm transition-shadow"
                  placeholder="e.g. EX00026143"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1.5">Alternative</label>
                <input
                  type="text"
                  value={createInstanceForm.alternative}
                  onChange={(e) => setCreateInstanceForm((f) => ({ ...f, alternative: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-gray-900 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/60 focus:border-blue-500/50 text-sm transition-shadow"
                  placeholder="e.g. 1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1.5">Revision</label>
                <input
                  type="text"
                  value={createInstanceForm.revision}
                  onChange={(e) => setCreateInstanceForm((f) => ({ ...f, revision: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-gray-900 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/60 focus:border-blue-500/50 text-sm transition-shadow"
                  placeholder="e.g. 1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1.5">Description</label>
                <input
                  type="text"
                  value={createInstanceForm.description}
                  onChange={(e) => setCreateInstanceForm((f) => ({ ...f, description: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-gray-900 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/60 focus:border-blue-500/50 text-sm transition-shadow"
                  placeholder="Optional"
                />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-400 mb-2">Parameter values</p>
                <div className="space-y-2">
                  {(createInstanceTemplate.parameters || []).map((p) => {
                    const paramId = p.id ?? p.ID;
                    const displayValue = createInstanceForm.values[paramId] ?? '';
                    const isLocked = createInstancePlcValuesLoaded;
                    return (
                      <div key={paramId} className="flex items-center gap-2">
                        <label className="w-40 shrink-0 text-gray-300 text-sm truncate" title={p.name}>
                          {p.name}
                        </label>
                        {isLocked ? (
                          <span className="flex-1 px-3 py-2.5 bg-gray-950 border border-gray-700 rounded-xl text-emerald-200/90 text-sm font-mono">
                            {displayValue || '—'}
                          </span>
                        ) : (
                          <input
                            type="text"
                            value={displayValue}
                            onChange={(e) =>
                              setCreateInstanceForm((f) => ({
                                ...f,
                                values: { ...f.values, [paramId]: e.target.value },
                              }))
                            }
                            className="flex-1 px-3 py-2.5 bg-gray-900 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/60 focus:border-blue-500/50 text-sm font-mono transition-shadow"
                            placeholder="Value"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              {createInstanceMutation.isError && (
                <p className="text-red-400 text-sm">{createInstanceMutation.error?.response?.data?.error ?? createInstanceMutation.error?.message ?? 'Error creating instance'}</p>
              )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-700/80 bg-gray-900/30">
              <button type="button" onClick={handleCloseCreateInstance} className="px-4 py-2.5 rounded-xl border border-gray-600 text-gray-300 hover:bg-gray-700/80 hover:text-white transition-colors text-sm font-medium">
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const templateParameters = createInstanceTemplate.parameters || [];
                  createInstanceMutation.mutate({
                    template_id: createInstanceTemplate.id,
                    product_name: createInstanceForm.product_name,
                    alternative: createInstanceForm.alternative,
                    revision: createInstanceForm.revision,
                    description: createInstanceForm.description,
                    values: createInstanceForm.values,
                    templateParameters,
                    sourceInstanceId: createInstanceSourceId
                      ? Number(createInstanceSourceId)
                      : null,
                  });
                }}
                disabled={
                  createInstanceMutation.isPending ||
                  loadPlcValuesForCreateMutation.isPending ||
                  !createInstanceForm.product_name.trim() ||
                  !createInstanceForm.alternative.trim() ||
                  !createInstanceForm.revision.trim() ||
                  (!!createInstanceSourceId && !createInstancePlcValuesLoaded)
                }
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50 text-sm font-medium transition-colors"
              >
                {createInstanceMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Create instance
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatosMaestrosProcesos;
